import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { useMessages } from '../Messages';
import './CreateProductionOrdersModal.css';

// Quantities can carry decimals; accept a comma as decimal separator
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(String(value).trim().replace(',', '.'));
};

const pendingQuantity = (line) => parseFloat(line.quantity_to_produce) || 0;

/**
 * Creates one production order per selected sales order line (POST
 * /api/enhanced/sales-orders/{id}/create-production-orders). `lines` are the lines still to produce
 * without a production order; each one starts selected with its full quantity to produce.
 */
function CreateProductionOrdersModal({ salesOrder, lines, onClose, onSuccess }) {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError, showWarning } = useMessages();
  const [selected, setSelected] = useState(() => lines.map(line => line.sales_order_line_id));
  const [quantities, setQuantities] = useState(() =>
    Object.fromEntries(lines.map(line => [line.sales_order_line_id, String(pendingQuantity(line))])));
  const [creating, setCreating] = useState(false);

  const toggle = (lineId) => setSelected(prev =>
    prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]);
  const toggleAll = () => setSelected(selected.length === lines.length ? [] : lines.map(line => line.sales_order_line_id));

  const handleCreate = async () => {
    if (selected.length === 0) {
      showError(t('createProductionOrdersModal.title'), t('createProductionOrdersModal.messages.noLinesSelected'));
      return;
    }
    const payload = [];
    for (const line of lines) {
      if (!selected.includes(line.sales_order_line_id)) continue;
      const quantity = toNumber(quantities[line.sales_order_line_id]);
      if (!(quantity > 0) || quantity > pendingQuantity(line)) {
        showError(t('createProductionOrdersModal.title'), t('createProductionOrdersModal.messages.invalidQuantity', {
          line: line.line_number, product: line.product_code || line.product_name, max: pendingQuantity(line)
        }));
        return;
      }
      payload.push({ line_id: line.sales_order_line_id, quantity });
    }

    setCreating(true);
    try {
      const response = await apiFetch(
        getApiUrl(`/api/enhanced/sales-orders/${salesOrder.sales_order_id}/create-production-orders`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line_ids: payload.map(p => p.line_id), lines: payload })
        });
      if (!response.ok) {
        await showApiError(response, showError, tCommon, t('createProductionOrdersModal.messages.failed'));
        return;
      }
      const result = await response.json();
      const created = result.production_orders || [];
      if (created.length === 0) {
        showWarning(t('createProductionOrdersModal.title'), t('createProductionOrdersModal.messages.noneCreated'));
      } else {
        showSuccess(t('createProductionOrdersModal.messages.createdTitle'), t('createProductionOrdersModal.messages.created', {
          numbers: created.map(po => po.po_number).join(', ')
        }));
      }
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (error) {
      console.error('Error creating production orders:', error);
      showError(t('createProductionOrdersModal.messages.failed'), error.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container production-orders-modal" role="dialog" aria-label={t('createProductionOrdersModal.title')}>
        <div className="modal-header">
          <h2>{t('createProductionOrdersModal.title')}</h2>
          <button className="close-btn" onClick={onClose} aria-label={tCommon('ui.close')}>×</button>
        </div>

        <div className="modal-body">
          <div className="lines-selection">
            <p>{t('createProductionOrdersModal.hint', { orderNumber: salesOrder.so_number })}</p>
            <table className="lines-table">
              <thead>
                <tr>
                  <th width="40">
                    <input
                      type="checkbox"
                      aria-label={t('createProductionOrdersModal.selectAll')}
                      checked={selected.length === lines.length && lines.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>{t('createProductionOrdersModal.columns.line')}</th>
                  <th>{t('createProductionOrdersModal.columns.product')}</th>
                  <th>{t('createProductionOrdersModal.columns.ordered')}</th>
                  <th>{t('createProductionOrdersModal.columns.pending')}</th>
                  <th>{t('createProductionOrdersModal.columns.quantity')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(line => {
                  const isSelected = selected.includes(line.sales_order_line_id);
                  return (
                    <tr key={line.sales_order_line_id} className={isSelected ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('createProductionOrdersModal.selectLine', { line: line.line_number })}
                          checked={isSelected}
                          onChange={() => toggle(line.sales_order_line_id)}
                        />
                      </td>
                      <td>{line.line_number}</td>
                      <td>
                        <div className="product-info">
                          <strong>{line.product_code}</strong>
                          <div className="product-name">{line.product_name}</div>
                        </div>
                      </td>
                      <td className="text-right">{parseFloat(line.quantity_ordered) || 0} {line.uom_code}</td>
                      <td className="text-right">{pendingQuantity(line)} {line.uom_code}</td>
                      <td className="text-right">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          max={pendingQuantity(line)}
                          aria-label={t('createProductionOrdersModal.quantityFor', { line: line.line_number })}
                          className="quantity-input"
                          value={quantities[line.sales_order_line_id] ?? ''}
                          onChange={(e) => setQuantities(prev => ({ ...prev, [line.sales_order_line_id]: e.target.value }))}
                          disabled={!isSelected}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={creating}>
            {t('createProductionOrdersModal.buttons.cancel')}
          </button>
          <button className="btn-primary" onClick={handleCreate} disabled={creating || selected.length === 0}>
            {creating
              ? t('createProductionOrdersModal.buttons.creating')
              : t('createProductionOrdersModal.buttons.create', { count: selected.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateProductionOrdersModal;
